import { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { addDays, format, parseISO, isValid, isSameDay } from 'date-fns';
import csLocale from 'date-fns/locale/cs';
import { Container, Box, Typography, Button, Chip, Snackbar, Alert } from '@mui/material';
import AuthContext from '../contexts/AuthContext';
import { useAxios } from '../hooks/useAxios';
import { useUnreadMessages } from '../hooks/useUnreadMessages';
import {
  useEmployeeCalendarData,
  getMondayWeekStart,
  formatDayKey,
} from '../hooks/useEmployeeCalendarData';
import { getTodayStart, isAssignedAvailability } from '../utils/employeeCalendarUtils';
import { getIsoDate } from '../utils/dateHandler';
import MobileWeekChrome, { jumpWeekToDay } from './MobileWeekChrome';
import { useEmployeeShiftDialogs } from './EmployeeShiftDialogs';

const MobileMyShifts = () => {
  const todayStart = getTodayStart();
  const [weekStart, setWeekStart] = useState(() => getMondayWeekStart(todayStart));
  const [selectedDay, setSelectedDay] = useState(todayStart);

  const { token } = useContext(AuthContext);
  const { availabilities, offeredAllocations, openAllocations, refresh, weekDays, visibleMonth } =
    useEmployeeCalendarData(weekStart);

  const { openAvailabilityDialog, dialogs } = useEmployeeShiftDialogs(refresh);
  const { requestStatus, sendRequest, clearRequestStatus } = useAxios();
  const { refreshEventName } = useUnreadMessages();
  const history = useHistory();
  const location = useLocation();

  const [notificationCounts, setNotificationCounts] = useState({});
  const daySectionRefs = useRef({});
  const weekStripRef = useRef(null);
  const deepLinkHandled = useRef(false);
  const initialScrollDone = useRef(false);

  const scrollToDay = useCallback((day) => {
    const key = formatDayKey(day);
    const el = daySectionRefs.current[key];
    if (!el) return;

    const header = document.querySelector('.glass-app-header');
    const strip = weekStripRef.current;
    const stickyOffset = (header?.offsetHeight || 0) + (strip?.offsetHeight || 0) + 8;
    document.documentElement.style.setProperty('--glass-day-scroll-margin', `${stickyOffset}px`);
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const scheduleScrollToDay = useCallback(
    (day) => {
      // Wait for paint so sticky chrome heights are accurate after selection.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => scrollToDay(day));
      });
    },
    [scrollToDay]
  );

  const handleJumpToDay = useCallback(
    (day) => {
      jumpWeekToDay(day, setWeekStart, setSelectedDay);
      scheduleScrollToDay(day);
    },
    [scheduleScrollToDay]
  );

  const handleSelectDay = useCallback(
    (day) => {
      setSelectedDay(day);
      scheduleScrollToDay(day);
    },
    [scheduleScrollToDay]
  );

  const handlePreviousWeek = () => setWeekStart((start) => addDays(start, -7));
  const handleNextWeek = () => setWeekStart((start) => addDays(start, 7));

  useEffect(() => {
    if (deepLinkHandled.current) return;
    const params = new URLSearchParams(location.search);
    const dateParam = params.get('date');
    if (!dateParam) return;

    const parsed = parseISO(dateParam);
    if (!isValid(parsed)) return;

    deepLinkHandled.current = true;
    const day = new Date(parsed);
    day.setHours(0, 0, 0, 0);
    handleJumpToDay(day);
  }, [location.search, handleJumpToDay]);

  useEffect(() => {
    if (initialScrollDone.current) return;
    initialScrollDone.current = true;
    scheduleScrollToDay(todayStart);
  }, [scheduleScrollToDay, todayStart]);

  const getNotificationCounts = useCallback(async () => {
    if (!token) return;
    try {
      const response = await sendRequest('/messages?isRead=false', 'GET', null, {
        Authorization: `Bearer ${token}`,
      });
      const counts = {};
      (response?.messages || []).forEach((message) => {
        if (!message?.relatedDate) return;
        const entityType = message.relatedEntity?.entityType;
        const isShiftRelated =
          message.type === 'shift' ||
          ['shiftAllocation', 'employeeAvailability'].includes(entityType);
        if (!isShiftRelated) return;
        const key = format(new Date(message.relatedDate), 'yyyy-MM-dd');
        counts[key] = (counts[key] || 0) + 1;
      });
      setNotificationCounts(counts);
    } catch {
      setNotificationCounts({});
    }
  }, [token, sendRequest]);

  useEffect(() => {
    getNotificationCounts();
  }, [getNotificationCounts]);

  useEffect(() => {
    const handler = () => getNotificationCounts();
    window.addEventListener(refreshEventName, handler);
    return () => window.removeEventListener(refreshEventName, handler);
  }, [refreshEventName, getNotificationCounts]);

  const handleOpenMessages = (day) => {
    history.push(`/messages?date=${format(day, 'yyyy-MM-dd')}`);
  };

  return (
    <>
      <Container maxWidth="sm" sx={{ pb: 1.5 }}>
        <MobileWeekChrome
          weekStart={weekStart}
          selectedDay={selectedDay}
          onSelectDay={handleSelectDay}
          onPreviousWeek={handlePreviousWeek}
          onNextWeek={handleNextWeek}
          visibleMonth={visibleMonth}
          availabilities={availabilities}
          offeredAllocations={offeredAllocations}
          openAllocations={openAllocations}
          onJumpToDay={handleJumpToDay}
          stripRef={weekStripRef}
        />

        <Box className="glass-week-day-list">
          {weekDays.map((day) => {
            const dayKey = formatDayKey(day);
            const dayAvailabilities = availabilities[dayKey] || [];
            const assigned = dayAvailabilities.filter((a) => isAssignedAvailability(a));
            const availabilityOnly = dayAvailabilities.filter((a) => !isAssignedAvailability(a));
            const isPast = day < todayStart;
            const isToday = isSameDay(day, todayStart);
            const isSelected = isSameDay(day, selectedDay);
            const notifications = notificationCounts[dayKey] || 0;

            return (
              <Box
                key={dayKey}
                ref={(el) => {
                  if (el) daySectionRefs.current[dayKey] = el;
                }}
                className="glass-day-section"
                data-today={isToday ? 'true' : 'false'}
                data-selected={isSelected ? 'true' : 'false'}
                sx={{ opacity: isPast ? 0.75 : 1 }}
              >
                <Box className="glass-day-section-header">
                  <Typography className="glass-day-section-title">
                    {format(day, 'EEEE d.M.', { locale: csLocale })}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {notifications > 0 && (
                      <Chip
                        size="small"
                        clickable
                        component="button"
                        onClick={() => handleOpenMessages(day)}
                        label={notifications === 1 ? '1 zpráva' : `${notifications} zprávy`}
                        sx={{ cursor: 'pointer' }}
                      />
                    )}
                    {!isPast && (
                      <Button
                        size="small"
                        variant="contained"
                        className="glass-accent-fill"
                        onClick={() => openAvailabilityDialog(day)}
                      >
                        Chci pracovat
                      </Button>
                    )}
                  </Box>
                </Box>

                {assigned.map((availability) => {
                  const shift = availability.shiftAllocationId;
                  return (
                    <button
                      key={availability._id}
                      type="button"
                      className="glass-list-card glass-shift-fill glass-list-card-button"
                      onClick={() => openAvailabilityDialog(day, availability)}
                    >
                      {availability.isOfferRequested && (
                        <Box sx={{ mb: 0.4 }}>
                          <Chip size="small" label="Nabízená" className="glass-chip-offered" />
                        </Box>
                      )}
                      <Typography className="glass-list-card-time">
                        {format(getIsoDate(availability.startTime), 'HH:mm')} –{' '}
                        {format(getIsoDate(availability.endTime), 'HH:mm')}
                      </Typography>
                      {shift && (
                        <Typography className="glass-list-card-title">
                          {shift.name}
                          {shift.outlet?.name && ` · ${shift.outlet.name}`}
                        </Typography>
                      )}
                      {availability.isCancellationRequested && (
                        <Typography className="glass-list-card-meta">Žádost o zrušení</Typography>
                      )}
                    </button>
                  );
                })}

                {availabilityOnly.map((availability) => (
                  <button
                    key={availability._id}
                    type="button"
                    className="glass-availability-line"
                    onClick={() => openAvailabilityDialog(day, availability)}
                  >
                    <span className="glass-availability-line-label">Dostupnost:</span>
                    <span className="glass-availability-line-time">
                      {format(getIsoDate(availability.startTime), 'HH:mm')} –{' '}
                      {format(getIsoDate(availability.endTime), 'HH:mm')}
                    </span>
                  </button>
                ))}

                {assigned.length === 0 && availabilityOnly.length === 0 && (
                  <Typography className="glass-day-empty">Žádné směny ani dostupnost.</Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Container>

      {dialogs}

      <Snackbar
        open={!!requestStatus}
        autoHideDuration={requestStatus?.duration || 6000}
        onClose={clearRequestStatus}
      >
        <Alert
          severity={requestStatus?.severity || 'info'}
          onClose={clearRequestStatus}
          className="glass-alert-snackbar"
        >
          {requestStatus?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MobileMyShifts;
